/**
 * Optional Google Analytics 4 (gtag). All helpers no-op when
 * `VITE_GA_MEASUREMENT_ID` is unset so whitelabel builds stay tracker-free.
 * Never send PII (email, street address, tokens).
 */

export type AnalyticsParams = Record<string, string | number | boolean | undefined>

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim() || ''

let initialized = false

export function isAnalyticsEnabled(): boolean {
  return MEASUREMENT_ID.length > 0
}

export function initAnalytics(): void {
  if (!MEASUREMENT_ID || initialized || typeof document === 'undefined') return
  initialized = true

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args)
  }
  window.gtag('js', new Date())
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`
  document.head.appendChild(script)
}

export function trackPageView(path: string): void {
  if (!MEASUREMENT_ID || !window.gtag) return
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
  })
}

export function trackEvent(name: string, params?: AnalyticsParams): void {
  if (!MEASUREMENT_ID || !window.gtag) return
  const cleaned: Record<string, string | number | boolean> = {}
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) cleaned[key] = value
    }
  }
  window.gtag('event', name, cleaned)
}

/** Path-only redirect target; drop query/hash to avoid leaking tokens. */
export function analyticsPathOnly(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined
  try {
    if (raw.startsWith('http')) {
      return new URL(raw).pathname
    }
    return raw.split('?')[0]?.split('#')[0] || undefined
  } catch {
    return undefined
  }
}
